import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/solid-router";
import { AppShell } from "~/components/AppShell";
import { ensureAuthenticated } from "~/lib/auth";
import { AuthCallbackPage } from "~/pages/AuthCallback";
import { DirectoryPage } from "~/pages/Directory";
import { EditProfilePage } from "~/pages/EditProfile";
import { EditMemberProfilePage } from "~/pages/EditMemberProfile";
import { HomePage } from "~/pages/Home";
import { LoginPage } from "~/pages/Login";
import { MeetupListPage } from "~/pages/MeetupList";
import { MeetupDetailPage } from "~/pages/MeetupDetail";
import { ChatRoomPage } from "~/pages/ChatRoom";
import { rememberReturnTo } from "~/lib/auth";
import { connectBasePath } from "~/lib/base-path";
import { meetupPath, meetupSlugFromLocation } from "~/lib/meetup";
import { OnboardingPage } from "~/pages/Onboarding";
import { SetupProfilePage } from "~/pages/SetupProfile";
import { ConnectionsPage } from "~/pages/Connections";
import { ProfileDetailPage } from "~/pages/ProfileDetail";
import { BadgeScanPage } from "~/pages/BadgeScan";
import { SponsorLeadsPage } from "~/pages/SponsorLeads";
import { AssignTicketPage } from "~/pages/AssignTicket";
import { MyTicketsPage } from "~/pages/MyTickets";
import { ManageOrderPage } from "~/pages/ManageOrder";
import { HelpPage } from "~/pages/Help";
import { MePage } from "~/pages/Me";
import { EditProjectPage } from "~/pages/EditProject";
import { ProjectDetailPage } from "~/pages/ProjectDetail";
import { OpportunitiesPage } from "~/pages/Opportunities";
import { EditOpportunityPage } from "~/pages/EditOpportunity";
import { OpportunityDetailPage } from "~/pages/OpportunityDetail";
import { CompaniesPage } from "~/pages/Companies";
import { CompanyDetailPage } from "~/pages/CompanyDetail";

async function requireAuth() {
  if (!(await ensureAuthenticated())) {
    if (typeof window !== "undefined") {
      const slug = meetupSlugFromLocation(window.location.pathname, window.location.search);
      if (slug) {
        rememberReturnTo(meetupPath(slug));
        throw redirect({ to: "/login", search: { meetup: slug } });
      }
    }
    throw redirect({ to: "/login", search: { meetup: undefined } });
  }
}

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/setup",
  beforeLoad: requireAuth,
  component: SetupProfilePage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: requireAuth,
  component: HomePage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>) => ({
    meetup: typeof search.meetup === "string" ? search.meetup : undefined,
  }),
  beforeLoad: async ({ search }) => {
    if (await ensureAuthenticated()) {
      if (search.meetup) {
        throw redirect({ to: "/meetup/$slug", params: { slug: search.meetup } });
      }
      throw redirect({ to: "/" });
    }
  },
  component: LoginPage,
});

/** Public help — claim steps + FAQ. No login required. */
const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/help",
  component: HelpPage,
});

const faqAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/faq",
  beforeLoad: () => {
    throw redirect({ to: "/help" });
  },
  component: HelpPage,
});

/** Public per-seat assign form (no login; token is the credential). */
const assignTicketRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assign/$token",
  component: AssignTicketPage,
});

const myTicketsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tickets",
  beforeLoad: requireAuth,
  component: MyTicketsPage,
});

const manageOrderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tickets/$orderId",
  beforeLoad: requireAuth,
  component: ManageOrderPage,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: AuthCallbackPage,
});

const meetupListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/meetup",
  beforeLoad: requireAuth,
  component: MeetupListPage,
});

const meetupDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/meetup/$slug",
  beforeLoad: requireAuth,
  component: MeetupDetailPage,
});

const chatRoomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chat/$channelId",
  beforeLoad: requireAuth,
  component: ChatRoomPage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/$eventId",
  beforeLoad: requireAuth,
  component: OnboardingPage,
});

const communityDirectoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/directory",
  beforeLoad: requireAuth,
  component: DirectoryPage,
});

const directoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/directory/$eventId",
  beforeLoad: requireAuth,
  component: DirectoryPage,
});

const communityConnectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connections",
  beforeLoad: requireAuth,
  component: ConnectionsPage,
});

const connectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/directory/$eventId/connections",
  beforeLoad: requireAuth,
  component: ConnectionsPage,
});

const communityProfileDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile/$profileId",
  beforeLoad: requireAuth,
  component: ProfileDetailPage,
});

const profileDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/directory/$eventId/profile/$profileId",
  beforeLoad: requireAuth,
  component: ProfileDetailPage,
});

const editProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/directory/$eventId/edit",
  beforeLoad: requireAuth,
  component: EditProfilePage,
});

const meRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/me",
  beforeLoad: requireAuth,
  component: MePage,
});

const editMemberProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/me/edit",
  beforeLoad: requireAuth,
  component: EditMemberProfilePage,
});

const newProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/me/projects/new",
  beforeLoad: requireAuth,
  component: EditProjectPage,
});

const editProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/me/projects/$projectId",
  beforeLoad: requireAuth,
  component: EditProjectPage,
});

const projectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId",
  beforeLoad: requireAuth,
  component: ProjectDetailPage,
});

const opportunitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/opportunities",
  beforeLoad: requireAuth,
  component: OpportunitiesPage,
});

const newOpportunityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/opportunities/new",
  beforeLoad: requireAuth,
  component: EditOpportunityPage,
});

const opportunityDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/opportunities/$opportunityId",
  beforeLoad: requireAuth,
  component: OpportunityDetailPage,
});

const companiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/companies",
  beforeLoad: requireAuth,
  component: CompaniesPage,
});

const companyDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/companies/$slug",
  beforeLoad: requireAuth,
  component: CompanyDetailPage,
});

const badgeScanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/directory/$eventId/scan",
  beforeLoad: requireAuth,
  component: BadgeScanPage,
});

const sponsorLeadsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/directory/$eventId/leads",
  beforeLoad: requireAuth,
  component: SponsorLeadsPage,
});

const routeTree = rootRoute.addChildren([
  setupRoute,
  indexRoute,
  loginRoute,
  helpRoute,
  faqAliasRoute,
  assignTicketRoute,
  myTicketsRoute,
  manageOrderRoute,
  authCallbackRoute,
  meetupListRoute,
  meetupDetailRoute,
  chatRoomRoute,
  onboardingRoute,
  communityDirectoryRoute,
  directoryRoute,
  communityConnectionsRoute,
  connectionsRoute,
  communityProfileDetailRoute,
  profileDetailRoute,
  editProfileRoute,
  meRoute,
  editMemberProfileRoute,
  newProjectRoute,
  editProjectRoute,
  projectDetailRoute,
  opportunitiesRoute,
  newOpportunityRoute,
  opportunityDetailRoute,
  companiesRoute,
  companyDetailRoute,
  badgeScanRoute,
  sponsorLeadsRoute,
]);

const basepath = connectBasePath();

export const router = createRouter({
  routeTree,
  ...(basepath ? { basepath } : {}),
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}